import React, { useState, useEffect, useRef } from 'react';
import { useRecoilValue } from 'recoil';
import { Table, Button, Space, Modal, Input, Select, message, Tooltip } from 'antd';
import { SearchOutlined, DeleteFilled, IdcardOutlined, EditOutlined, LoadingOutlined } from '@ant-design/icons';
import Highlighter from 'react-highlight-words';
import dayjs from '_helpers/datetime';

import { AddStudentForm, StudentProfileCard } from '_components/studentInfoList';
import { useStudentInfoAction } from '_actions';
import DatePicker from '_helpers/DatePicker';
import { ROLES } from '_helpers/roles';
import { currentClassAtom } from '_state';

export { StudentInfoTable };

function StudentInfoTable(props) {
    const studentInfoAction  = useStudentInfoAction();
    const currentClass       = useRecoilValue(currentClassAtom);
    const userData           = JSON.parse(localStorage.getItem('userData'));
    const isArchived         = !!currentClass?.archived;

    // ── search state ──────────────────────────────────────────────────────────
    const [searchText,   setSearchText]   = useState('');
    const [searchedCol,  setSearchedCol]  = useState('');

    // ── modal state ───────────────────────────────────────────────────────────
    const [visibleAdding,  setVisibleAdding]  = useState(false);
    const [deleteTarget,   setDeleteTarget]   = useState(null); // record to delete
    const [profileTarget,  setProfileTarget]  = useState(null); // record whose profile modal is open

    // ── inline-edit state ─────────────────────────────────────────────────────
    // { _id, field, value, originalValue } — value is always API-ready.
    const [editCell,   setEditCell]   = useState(null);
    const [savingKey,  setSavingKey]  = useState(null);
    const [editError,  setEditError]  = useState('');
    const savingKeyRef = useRef(null);
    const [sortedInfo, setSortedInfo] = useState({ columnKey: 'name', order: 'ascend' });
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    useEffect(() => {
        const handler = () => setIsMobile(window.innerWidth <= 768);
        window.addEventListener('resize', handler);
        return () => window.removeEventListener('resize', handler);
    }, []);

    // Process display data (gender & date labels)
    const data = props.data.map(s => ({
        ...s,
        _genderRaw: s.gender,           // keep raw for editing
        _dobRaw:    s.date_of_birth,    // keep raw timestamp for editing
        gender:     s.gender === 'male' ? 'Nam' : s.gender === 'female' ? 'Nữ' : '',
        date_of_birth: s.date_of_birth
            ? dayjs(s.date_of_birth).format('DD/MM/YYYY')
            : '',
    }));

    // ── save inline edit ──────────────────────────────────────────────────────
    const cellKey = (cell) => cell ? `${cell._id}:${cell.field}` : '';
    const sameValue = (cell) => {
        if (cell.field === 'date_of_birth') {
            const a = cell.originalValue ? dayjs(cell.originalValue).format('YYYY-MM-DD') : '';
            const b = cell.value ? dayjs(cell.value).format('YYYY-MM-DD') : '';
            return a === b;
        }
        return String(cell.value ?? '') === String(cell.originalValue ?? '');
    };

    async function saveEdit(cell) {
        if (!cell) return;
        const key = cellKey(cell);
        if (savingKeyRef.current === key) return;
        if (sameValue(cell)) {
            setEditCell(null);
            setEditError('');
            return;
        }
        savingKeyRef.current = key;
        setSavingKey(key);
        setEditError('');
        try {
            const json = await studentInfoAction.updateStudent(cell._id, { [cell.field]: cell.value ?? '' });
            if (json.status === 'Success') {
                await studentInfoAction.refresh();
                setEditCell(cur => cellKey(cur) === key ? null : cur);
                message.success({ content: 'Đã lưu', key: 'student-inline-save' });
            } else {
                setEditError(json.message || 'Lỗi khi lưu');
                message.error(json.message || 'Lỗi khi lưu');
            }
        } catch (e) {
            const msg = 'Lỗi khi lưu: ' + e.message;
            setEditError(msg);
            message.error(msg);
        } finally {
            savingKeyRef.current = null;
            setSavingKey(null);
        }
    }

    function startEdit(record, field, initialValue) {
        if (savingKey || isArchived) return;
        const value = initialValue ?? '';
        setEditError('');
        setEditCell({ _id: record._id, field, value, originalValue: value });
    }

    function cancelEdit() {
        if (savingKey) return;
        setEditCell(null);
        setEditError('');
    }

    function editableDisplay(record, field, displayValue, rawValue, disabled) {
        const active = editCell?._id === record._id && editCell?.field === field;
        const key = `${record._id}:${field}`;
        const isSaving = savingKey === key;
        const content = displayValue || <span style={{ color: 'var(--ink-4)' }}>—</span>;
        if (disabled || isArchived) return <span className="si-readonly-cell">{content}</span>;
        if (active) return null;
        return (
            <Tooltip title="Nhấp để chỉnh sửa" mouseEnterDelay={0.6}>
                <button
                    type="button"
                    className="si-editable-cell"
                    onClick={() => startEdit(record, field, rawValue ?? displayValue ?? '')}
                >
                    <span className="si-editable-value">{content}</span>
                    {isSaving ? <LoadingOutlined className="si-edit-icon is-saving" /> : <EditOutlined className="si-edit-icon" />}
                </button>
            </Tooltip>
        );
    }

    function activeEditShell(record, field, control) {
        const key = `${record._id}:${field}`;
        const showError = editError && savingKey !== key;
        return (
            <div className="si-edit-active">
                {control}
                {savingKey === key && <span className="si-edit-state"><LoadingOutlined /> Đang lưu</span>}
                {showError && <span className="si-edit-error">{editError}</span>}
            </div>
        );
    }

    // ── renders an inline-editable text cell ──────────────────────────────────
    function editableText(record, field, displayValue, rawValue) {
        const active = editCell?._id === record._id && editCell?.field === field;
        if (active) {
            return activeEditShell(record, field, (
                <Input
                    autoFocus
                    size="small"
                    value={editCell.value}
                    onChange={e => setEditCell({ ...editCell, value: e.target.value })}
                    onBlur={() => saveEdit(editCell)}
                    onPressEnter={() => saveEdit(editCell)}
                    onKeyDown={e => { if (e.key === 'Escape') cancelEdit(); }}
                    disabled={savingKey === `${record._id}:${field}`}
                    style={{ minWidth: 80 }}
                />
            ));
        }
        return editableDisplay(record, field, displayValue, rawValue);
    }

    function editableDate(record) {
        const active = editCell?._id === record._id && editCell?.field === 'date_of_birth';
        if (active) {
            return activeEditShell(record, 'date_of_birth', (
                <DatePicker
                    autoFocus
                    size="small"
                    format="DD/MM/YYYY"
                    value={editCell.value ? dayjs(editCell.value) : null}
                    onChange={v => {
                        const next = { ...editCell, value: v ? v.valueOf() : '' };
                        setEditCell(next);
                        saveEdit(next);
                    }}
                    onKeyDown={e => { if (e.key === 'Escape') cancelEdit(); }}
                    disabled={savingKey === `${record._id}:date_of_birth`}
                    style={{ width: '100%' }}
                />
            ));
        }
        return editableDisplay(record, 'date_of_birth', record.date_of_birth, record._dobRaw);
    }

    // ── renders an inline-editable gender Select ──────────────────────────────
    function editableGender(record) {
        const active = editCell?._id === record._id && editCell?.field === 'gender';
        if (active) {
            return activeEditShell(record, 'gender', (
                <Select
                    autoFocus
                    defaultOpen
                    size="small"
                    value={editCell.value}
                    style={{ width: 80 }}
                    onChange={v => {
                        const next = { ...editCell, value: v };
                        setEditCell(next);
                        saveEdit(next);
                    }}
                    onKeyDown={e => { if (e.key === 'Escape') cancelEdit(); }}
                    disabled={savingKey === `${record._id}:gender`}
                >
                    <Select.Option value="male">Nam</Select.Option>
                    <Select.Option value="female">Nữ</Select.Option>
                </Select>
            ));
        }
        return editableDisplay(record, 'gender', record.gender, record._genderRaw || '');
    }

    // ── column search ─────────────────────────────────────────────────────────
    let searchInput;
    function getColumnSearchProps(dataIndex) {
        return {
            filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
                <div style={{ padding: 8 }}>
                    <Input
                        ref={node => { searchInput = node; }}
                        placeholder={`Tìm ${dataIndex}`}
                        value={selectedKeys[0]}
                        onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                        onPressEnter={() => { confirm(); setSearchText(selectedKeys[0]); setSearchedCol(dataIndex); }}
                        style={{ marginBottom: 8, display: 'block' }}
                    />
                    <Space>
                        <Button
                            type="primary"
                            icon={<SearchOutlined />}
                            size="small"
                            style={{ width: 90 }}
                            onClick={() => { confirm(); setSearchText(selectedKeys[0]); setSearchedCol(dataIndex); }}
                        >Tìm</Button>
                        <Button size="small" style={{ width: 90 }} onClick={() => { clearFilters(); setSearchText(''); }}>
                            Reset
                        </Button>
                    </Space>
                </div>
            ),
            filterIcon: filtered => <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />,
            onFilter: (value, record) =>
                record[dataIndex]?.toString().toLowerCase().includes(value.toLowerCase()),
            onFilterDropdownVisibleChange: visible => {
                if (visible) setTimeout(() => searchInput?.select(), 100);
            },
            render: text =>
                searchedCol === dataIndex ? (
                    <Highlighter
                        highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
                        searchWords={[searchText]}
                        autoEscape
                        textToHighlight={text ? text.toString() : ''}
                    />
                ) : text,
        };
    }

    // ── columns ───────────────────────────────────────────────────────────────
    const columns = [
        {
            title: 'Họ và tên',
            dataIndex: 'name',
            key: 'name',
            width: isMobile ? 120 : 140,
            fixed: 'left',
            ...getColumnSearchProps('name'),
            sortOrder: sortedInfo.columnKey === 'name' ? sortedInfo.order : null,
            sorter: (a, b) => {
                const viMap = {'à':'a','á':'a','ả':'a','ã':'a','ạ':'a','ă':'a','ắ':'a','ằ':'a','ẳ':'a','ẵ':'a','ặ':'a','â':'a','ấ':'a','ầ':'a','ẩ':'a','ẫ':'a','ậ':'a','è':'e','é':'e','ẻ':'e','ẽ':'e','ẹ':'e','ê':'e','ế':'e','ề':'e','ể':'e','ễ':'e','ệ':'e','ì':'i','í':'i','ỉ':'i','ĩ':'i','ị':'i','ò':'o','ó':'o','ỏ':'o','õ':'o','ọ':'o','ô':'o','ố':'o','ồ':'o','ổ':'o','ỗ':'o','ộ':'o','ơ':'o','ớ':'o','ờ':'o','ở':'o','ỡ':'o','ợ':'o','ù':'u','ú':'u','ủ':'u','ũ':'u','ụ':'u','ư':'u','ứ':'u','ừ':'u','ử':'u','ữ':'u','ự':'u','ỳ':'y','ý':'y','ỷ':'y','ỹ':'y','ỵ':'y','đ':'d'};
                const norm = s => (s || '').toLowerCase().split('').map(c => viMap[c] || c).join('');
                const last = name => norm((name || '').trim().split(' ').pop());
                return last(a.name) < last(b.name) ? -1 : 1;
            },
            render: (text, record) => isMobile ? (
                <div style={{ minWidth: 0 }}>
                    {editableText(record, 'name', text, text)}
                    <div style={{ color: 'var(--ink-4)', fontSize: 11, marginTop: 1 }}>
                        {record.student_id || '—'}
                    </div>
                </div>
            ) : editableText(record, 'name', text, text),
        },
        {
            title: 'Mã học sinh',
            dataIndex: 'student_id',
            key: 'student_id',
            width: 110,
            responsive: ['md', 'lg', 'xl', 'xxl'],
            ...getColumnSearchProps('student_id'),
            sortOrder: sortedInfo.columnKey === 'student_id' ? sortedInfo.order : null,
            sorter: (a, b) => (a.student_id || '').localeCompare(b.student_id || '', undefined, { numeric: true }),
            render: (text, record) => userData?.role === ROLES.MANAGER
                ? editableText(record, 'student_id', text, text)
                : (text || <span style={{ color: 'var(--ink-4)' }}>—</span>),
        },
        {
            title: 'Giới tính',
            dataIndex: 'gender',
            key: 'gender',
            width: 85,
            render: (_, record) => editableGender(record),
        },
        {
            title: 'Ngày sinh',
            dataIndex: 'date_of_birth',
            key: 'date_of_birth',
            width: 120,
            render: (_, record) => editableDate(record),
        },
        {
            title: 'SĐT phụ huynh',
            dataIndex: 'parent_number',
            key: 'parent_number',
            width: 130,
            render: (text, record) => editableText(record, 'parent_number', text, text),
        },
        {
            title: 'Địa chỉ',
            dataIndex: 'location',
            key: 'location',
            width: 160,
            render: (text, record) => editableText(record, 'location', text, text),
        },
        {
            title: 'Email',
            dataIndex: 'email',
            key: 'email',
            width: 160,
            render: (text, record) => editableText(record, 'email', text, text),
        },
        {
            title: 'Số điện thoại',
            dataIndex: 'phone_number',
            key: 'phone_number',
            width: 130,
            render: (text, record) => editableText(record, 'phone_number', text, text),
        },
        {
            title: 'Thao tác',
            key: 'action',
            fixed: 'right',
            width: 96,
            responsive: ['md', 'lg', 'xl', 'xxl'],
            align: 'center',
            render: (_, record) => (
                <Space size={4}>
                    <Button
                        title="Xem hồ sơ"
                        // Pass RAW timestamp/gender — the table row maps these to display
                        // strings, but StudentProfileCard re-formats from raw values.
                        onClick={() => setProfileTarget({
                            ...record,
                            date_of_birth: record._dobRaw,
                            gender:        record._genderRaw,
                        })}
                        icon={<IdcardOutlined />}
                        size="small"
                    />
                    {!isArchived && (
                        <Button
                            danger
                            type="primary"
                            title="Xóa khỏi lớp"
                            onClick={() => setDeleteTarget(record)}
                            icon={<DeleteFilled />}
                            size="small"
                        />
                    )}
                </Space>
            ),
        },
    ];

    return (
        <div className="att2-page soft-in">
            <div className="att2-head">
                <div className="att2-head-l">
                    <h1 className="att2-title">Thông tin liên hệ học sinh{isArchived && <span className="tag t-neutral att2-title-tag">Chỉ đọc</span>}</h1>
                    <div className="att2-sub">{isArchived ? 'Lớp đã lưu trữ, chỉ xem thông tin học sinh' : 'Nhấp vào ô có biểu tượng bút để chỉnh sửa trực tiếp'}</div>
                </div>
                {!isArchived && <div className="att2-tools">
                    <Button type="primary" onClick={() => setVisibleAdding(true)}>
                        Thêm học sinh
                    </Button>
                </div>}
            </div>

            <div className="float-panel soft-in-scale">
                <Table
                    columns={columns}
                    dataSource={data.map(s => ({ ...s, key: s._id }))}
                    size="middle"
                    scroll={{ x: isMobile ? 'max-content' : 'calc(700px + 50%)' }}
                    onChange={(_, __, sorter) => {
                        const s = Array.isArray(sorter) ? sorter[0] : sorter;
                        setSortedInfo(s?.order ? s : {});
                    }}
                />
            </div>

            {/* Add student modal */}
            <Modal
                title="Thêm học sinh"
                visible={visibleAdding}
                onCancel={() => setVisibleAdding(false)}
                footer={[]}
            >
                <AddStudentForm onSuccess={() => {
                    setVisibleAdding(false);
                    studentInfoAction.refresh();
                }} />
            </Modal>

            {/* Student profile modal */}
            <Modal
                title="Hồ sơ học sinh"
                visible={!!profileTarget}
                onCancel={() => setProfileTarget(null)}
                footer={null}
                width={680}
                bodyStyle={{ paddingTop: 8 }}
                destroyOnClose
            >
                {profileTarget && (
                    <StudentProfileCard
                        data={profileTarget}
                        classInfo={currentClass}
                        onUpdate={() => studentInfoAction.refresh()}
                    />
                )}
            </Modal>

            {/* Delete confirm modal */}
            <Modal
                title="Bạn có chắc là xóa thành viên này?"
                visible={!!deleteTarget}
                onOk={() => {
                    studentInfoAction.deleteStudent(deleteTarget.student_id);
                    setDeleteTarget(null);
                }}
                onCancel={() => setDeleteTarget(null)}
                okText="Xác nhận"
                okType="danger"
                cancelText="Hủy bỏ"
            >
                Xác nhận sẽ xóa thành viên <b>{deleteTarget?.name}</b> ra khỏi lớp!
            </Modal>
        </div>
    );
}
